import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { Attribute } from 'src/app/models/attribute';
import { Rights } from 'src/app/models/enums/rights';
import { ISortable } from 'src/app/models/interfaces/sortable.interface';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';

@Component({
    selector: 'app-attributes-list',
    templateUrl: './attributes-list.component.html',
    styleUrls: ['./attributes-list.component.scss'],
    imports: [AdvancedGridviewComponent]
})
export class AttributesListComponent extends DefaultListComponent<Attribute> implements OnInit, AfterViewInit {
    typeColumnValues: ListItem<string>[] = [
        new ListItem('0', 'Aucun'),
        new ListItem('1', 'Date'),
        new ListItem('2', 'Texte'),
        new ListItem('3', 'Nombre entier'),
        new ListItem('11', 'Nombre décimal'),
        new ListItem('4', 'Liste'),
        new ListItem('5', 'Couleur'),
        new ListItem('6', 'Workflow'),
        new ListItem('7', 'Enumération'),
        new ListItem('8', 'Date & heure'),
        new ListItem('9', 'Clients'),
        new ListItem('10', 'Utilisateurs'),
        new ListItem('12', 'Email'),
        new ListItem('13', 'Lien'),
        new ListItem('14', 'Autocompletion'),
        new ListItem('15', 'N° téléphone'),
    ];

    displayFormatColumnValues: ListItem<string>[] = [
        new ListItem('1', 'Jour/Mois/Année'),
        new ListItem('2', 'Année-Mois-jour'),
        new ListItem('6', 'Jour/Mois'),
        new ListItem('3', 'Jour/Mois/Année Heures:Minutes:secondes'),
        new ListItem('4', 'Jour/Mois/Année Heures:Minutes'),
        new ListItem('5', 'Jour/Mois/Année Heures'),
        new ListItem('7', 'XXXXXXXXXX'),
        new ListItem('8', 'XX XX XX XX XX'),
        new ListItem('9', 'XX.XX.XX.XX.XX'),
        new ListItem('10', 'XX-XX-XX-XX-XX'),
    ];

    /**
     * Configuration des colonnes de la liste.
     */
    tableConfig: ColumnDefinition[] = null;

    public getDeletionConditionFunction: (item: Attribute) => boolean = (item: Attribute) => {
        return item.custom === true;
    };

    /**
     * Constructeur
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     * @param enumHelper : Classe utilitaire pour les énumérations.
     * @param attributesService : Service de gestion des attributs.
     */
    constructor(
        public override dialog: MatDialog,
        protected override memoryService: MemoryService,
        public enumHelper: EnumHelper,
        private attributesService: AttributeDefinitionsService,
        private spinner: NgxSpinnerService,
        private notificationService: NotificationService
    ) {
        super(dialog, memoryService);

        // Specifie des colonnes en fonction des droits utilisateurs
        if (this.memoryService.getConnectedUser().params.idVisible === true) {
            this.tableConfig = [
                new ColumnDefinition('id', 'Id', ColumnType.Numeric, FilterType.Text, 1, 4, true, null, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 10, true),
                new ColumnDefinition('header', 'En Tête', ColumnType.Text, FilterType.Text, 3, 10, true),
                new ColumnListDefinition('type', 'Type', FilterType.List, 4, 5, true, false, this.typeColumnValues),
                new ColumnDefinition('format', 'Format', ColumnType.Text, FilterType.Text, 5, 10, true),
                new ColumnListDefinition('displayFormat', "Format d'affichage", FilterType.List, 6, 10, true, false, this.displayFormatColumnValues),
                new ColumnDefinition('defaultValueDisplayed', 'Valeur par défaut', ColumnType.Text, FilterType.Text, 7, 6, true),
                new ColumnDefinition('order', 'Ordre', ColumnType.Numeric, FilterType.Numeric, 8, 5, true),
                new ColumnDefinition('enable', 'Actif', ColumnType.Boolean, FilterType.Boolean, 9, 5, true),
                new ColumnDefinition('custom', 'Personnalisé', ColumnType.Boolean, FilterType.Boolean, 10, 5, true),
                new ColumnDefinition('mandatory', 'Obligatoire', ColumnType.Boolean, FilterType.Boolean, 11, 5, true),
                new ColumnDefinition('isFavorite', 'Favoris', ColumnType.Boolean, FilterType.Boolean, 12, 5, true),
                new ColumnDefinition('displayedInCreationForm', 'Afficher création', ColumnType.Boolean, FilterType.Boolean, 13, 5, true),
                new ColumnDefinition('usedBySearch', 'Utilisé par recherche', ColumnType.Boolean, FilterType.Boolean, 14, 5, true),
                new ColumnDefinition('invisible', 'Non visible dans UI', ColumnType.Boolean, FilterType.Boolean, 15, 5, true),
            ];
        } else {
            this.tableConfig = [
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 10, true),
                new ColumnDefinition('header', 'En Tête', ColumnType.Text, FilterType.Text, 3, 10, true),
                new ColumnListDefinition('type', 'Type', FilterType.List, 4, 5, true, false, this.typeColumnValues),
                new ColumnDefinition('format', 'Format', ColumnType.Text, FilterType.Text, 5, 10, true),
                new ColumnListDefinition('displayFormat', "Format d'affichage", FilterType.List, 6, 10, true, false, this.displayFormatColumnValues),
                new ColumnDefinition('defaultValueDisplayed', 'Valeur par défaut', ColumnType.Text, FilterType.Text, 7, 7, true),
                new ColumnDefinition('order', 'Ordre', ColumnType.Numeric, FilterType.Numeric, 8, 5, true),
                new ColumnDefinition('enable', 'Actif', ColumnType.Boolean, FilterType.Boolean, 9, 5, true),
                new ColumnDefinition('custom', 'Personnalisé', ColumnType.Boolean, FilterType.Boolean, 10, 5, true),
                new ColumnDefinition('mandatory', 'Obligatoire', ColumnType.Boolean, FilterType.Boolean, 11, 5, true),
                new ColumnDefinition('isFavorite', 'Favoris', ColumnType.Boolean, FilterType.Boolean, 12, 5, true),
                new ColumnDefinition('displayedInCreationForm', 'Afficher création', ColumnType.Boolean, FilterType.Boolean, 13, 5, true),
                new ColumnDefinition('usedBySearch', 'Utilisé par recherche', ColumnType.Boolean, FilterType.Boolean, 14, 7, true),
                new ColumnDefinition('invisible', 'Non visible dans UI', ColumnType.Boolean, FilterType.Boolean, 15, 5, true),
            ];
        }

        this.AddMenuItem('Dupliquer', MenuITemAction.Duplicate, Rights.AttributCreate);
        this.AddMenuItem('Éditer', MenuITemAction.Update, Rights.AttributEdit);
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, Rights.AttributDelete, this.getDeletionConditionFunction);
    }

    ngAfterViewInit(): void {
        this.gridView.getSortableItemsFunction = this.getSortablesItems.bind(this);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.dataSource = new AdvancedDataSource(null, this.attributesService, this.spinner, this.notificationService);
        this.filteredTableModel = new AdvancedGridviewModel(this.tableConfig, new SortInfos('order', 'asc'));

        // Ajout des listes déroulantes
        // attente de 500ms pour que la gridView ait le temps de se remplir.
        setTimeout(() => {
            this.loadListColumnFilterDatas();
        }, 500);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<Attribute>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.itemDisplayed.emit(event.context);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.itemEdited.emit(event.context);
        } else if (event.action == MenuITemAction.Duplicate) {
            // Cas de duplication.
            this.itemDuplicated.emit(event.context);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(`Vous êtes sur le point de supprimer l'attribut '${event.context.name}'.`, event.context);
        }
    }

    /**
     * Charge les éléments des filtres des colonnes de type liste.
     */
    private loadListColumnFilterDatas(): void {
        var listItem: ListItem<string>[] = [];
        listItem.push(new ListItem('0', 'Aucun'));
        listItem.push(new ListItem('1', 'Date'));
        listItem.push(new ListItem('2', 'Texte'));
        listItem.push(new ListItem('3', 'Nombre entier'));
        listItem.push(new ListItem('11', 'Nombre décimal'));
        listItem.push(new ListItem('4', 'Liste'));
        listItem.push(new ListItem('5', 'Couleur'));
        listItem.push(new ListItem('6', 'Workflow'));
        listItem.push(new ListItem('7', 'Enumération'));
        listItem.push(new ListItem('8', 'Date & heure'));
        listItem.push(new ListItem('9', 'Clients'));
        listItem.push(new ListItem('10', 'Utilisateurs'));
        listItem.push(new ListItem('12', 'Email'));
        listItem.push(new ListItem('13', 'Lien'));
        this.loadColumn(
            this.filteredTableModel.displayedColumnsDefinitions.find(
                (x) => this.enumHelper.areEquals(ColumnType, x.type, ColumnType.List) && x.columnName === 'type'
            ) as ColumnListDefinition<string>,
            listItem
        );

        var formatlistItem: ListItem<string>[] = [];
        formatlistItem.push(new ListItem('1', 'Jour/Mois/Année'));
        formatlistItem.push(new ListItem('2', 'Année-Mois-jour'));
        formatlistItem.push(new ListItem('6', 'Jour/Mois'));
        formatlistItem.push(new ListItem('3', 'Jour/Mois/Année Heures:Minutes:secondes'));
        formatlistItem.push(new ListItem('4', 'Jour/Mois/Année Heures:Minutes'));
        formatlistItem.push(new ListItem('5', 'Jour/Mois/Année Heures'));

        this.loadColumn(
            this.filteredTableModel.displayedColumnsDefinitions.find(
                (x) => this.enumHelper.areEquals(ColumnType, x.type, ColumnType.List) && x.columnName === 'displayFormat'
            ) as ColumnListDefinition<string>,
            formatlistItem
        );
    }

    public onItemsSorted(event: ISortable[]) {
        //const maxOrder = this.dataSource.datas.filter(a => a.custom === false).sort((a, b) => { return a.order > b.order ? -1 : 1})[0].order;
        //event.forEach(a => a.order = a.order + maxOrder);
        this.itemsSorted.emit(event);
    }

    protected getSortablesItems(item: Attribute): Observable<Attribute[]> {
        // let filter = new FilterInfos();
        // filter.ColumnName = "custom";
        // filter.FilterType = FilterType.Boolean;
        // filter.Type = ColumnType.Boolean;
        // filter.Value = 'true';

        //return of(this.dataSource.datas.filter(a => a.custom === true).sort((a, b) => { return a.order < b.order ? -1 : 1 }));

        return of(
            this.dataSource.datas.sort((a, b) => {
                return a.order < b.order ? -1 : 1;
            })
        );
    }
}
